/**
 * Faculty options from Home formation section.
 * Shared by Alumni form and Dashboard filters.
 * value = DB/filter value, label = French UI, subdomain = route path
 */
export const FACULTY_OPTIONS = [
  { value: 'fdseg', label: "Faculté de Droit, Économie & Gestion", subdomain: 'faculte/fdseg' },
  { value: 'fllca', label: 'Faculté de Lettres, Langues et Arts', subdomain: 'faculte/fllca' },
  { value: 'medecine', label: 'Faculté de Médecine', subdomain: 'faculte/medecine' },
  { value: 'sav', label: "Faculté des Sciences Agronomiques et Vétérinaires (FSAV)", subdomain: 'faculte/sav' },
  { value: 'sciences', label: 'Faculté des Sciences et Technologies', subdomain: 'faculte/sciences' },
  { value: 'iscoba', label: 'ISCOBA', subdomain: 'institut/iscoba' },
  { value: 'issav', label: 'ISSAV', subdomain: 'institut/issav' },
  { value: 'issi', label: 'ISSI', subdomain: 'institut/issi' },
  { value: 'ipl', label: 'IPL', subdomain: 'institut/ipl' },
  { value: 'iuss', label: 'IUSS', subdomain: 'institut/iuss' },
  { value: 'nutrition', label: 'Master en Nutrition', subdomain: 'masters/nutrition' },
  { value: 'developpement-rural', label: 'Master en Développement Rural', subdomain: 'masters/developpement-rural' },
  { value: 'agribusiness', label: 'Master en Agribusiness', subdomain: 'masters/agribusiness' },
  { value: 'droit-public', label: 'Master en Droit Public', subdomain: 'masters/droit-public' },
  { value: 'droit-prive', label: 'Master en Droit Privé', subdomain: 'masters/droit-prive' },
  { value: 'politiques-economiques', label: 'Master en Politiques Économiques', subdomain: 'masters/politiques-economiques' },
  { value: 'management-projets', label: 'Master en Management des Projets', subdomain: 'masters/management-projets' },
  { value: 'banque-microfinance', label: 'Master en Banque & Microfinance', subdomain: 'masters/banque-microfinance' },
];

export function getFacultyByValue(value) {
  return FACULTY_OPTIONS.find((f) => f.value === value);
}

export function getSubdomainForFaculty(value) {
  const f = getFacultyByValue(value);
  return f ? f.subdomain : null;
}

/**
 * Formation programs for the Home page "Nos Programmes" section.
 * shortLabel = acronym/title on card, label = explanation (full name).
 * type: 'faculty' | 'institut' | 'master' for styling
 */
export const FORMATION_PROGRAMS = [
  // Facultés
  {
    value: 'fdseg',
    shortLabel: 'FDSEG',
    label: "Faculté de Droit, des Sciences Économiques et de Gestion",
    href: '/faculte/fdseg',
    type: 'faculty',
    description: "Formations d'excellence en droit, sciences économiques et gestion avec masters spécialisés.",
    highlights: ['Droit privé & public', 'Économie & Gestion', 'Comptabilité & Finance'],
  },
  {
    value: 'fllca',
    shortLabel: 'FLLCA',
    label: 'Faculté de Lettres, Langues, Communication & Arts',
    href: '/faculte/fllca',
    type: 'faculty',
    description: "Lettres, langues, communication et arts pour les créateurs et les professionnels de la parole.",
    highlights: ['Langues modernes', 'Communication', 'Littérature & Arts'],
  },
  {
    value: 'medecine',
    shortLabel: 'Médecine',
    label: 'Sciences de la Santé',
    href: '/faculte/medecine',
    type: 'faculty',
    description: "Formation en médecine et sciences de la santé pour les professionnels de demain.",
    highlights: ['Médecine', 'Sciences de la santé', 'Pratiques cliniques'],
  },
  {
    value: 'sav',
    shortLabel: 'FSAV',
    label: 'Sciences Agronomiques et Vétérinaires',
    href: '/faculte/sav',
    type: 'faculty',
    description: "Agronomie, élevage et santé animale au service du développement durable.",
    highlights: ['Agronomie', 'Sciences vétérinaires', 'Développement rural'],
  },
  {
    value: 'sciences',
    shortLabel: 'FST',
    label: 'Faculté des Sciences et Technologies',
    href: '/faculte/sciences',
    type: 'faculty',
    description: "Biologie, chimie, physique, mathématiques et sciences de l'ingénieur.",
    highlights: ['Sciences fondamentales', 'Laboratoires modernes', 'Projets & innovation'],
  },
  // Instituts
  {
    value: 'iscoba',
    shortLabel: 'ISCOBA',
    label: 'Comptabilité, Banque, Assurance',
    href: '/institut/iscoba',
    type: 'institut',
    description: "Institut supérieur de comptabilité, banque et assurance, formations professionnelles reconnues.",
    highlights: ['Comptabilité', 'Banque & Finance', 'Assurance'],
  },
  {
    value: 'issav',
    shortLabel: 'ISSAV',
    label: 'Sciences Agronomiques et Vétérinaires',
    href: '/institut/issav',
    type: 'institut',
    description: "Formations spécialisées en sciences agronomiques et vétérinaires.",
    highlights: ['Agronomie', 'Élevage', 'Santé animale'],
  },
  {
    value: 'issi',
    shortLabel: 'ISSI',
    label: 'Sciences Informatiques',
    href: '/institut/issi',
    type: 'institut',
    description: "Développement logiciel, réseaux, cybersécurité et technologies de l'information.",
    highlights: ['Développement logiciel', 'Réseaux & sécurité', 'Informatique appliquée'],
  },
  {
    value: 'ipl',
    shortLabel: 'IPL',
    label: 'Pédagogie des Langues – Anglais',
    href: '/institut/ipl',
    type: 'institut',
    description: "Pédagogie des langues avec option anglais pour l'enseignement et la communication.",
    highlights: ['Anglais', 'Pédagogie', 'Didactique des langues'],
  },
  {
    value: 'iuss',
    shortLabel: 'IUSS',
    label: 'Institut Universitaire des Sciences de la Santé',
    href: '/institut/iuss',
    type: 'institut',
    description: "Formation en soins infirmiers de niveau supérieur. Baccalauréat professionnel, 3 ans.",
    highlights: ['Soins infirmiers', 'Santé', 'BMD'],
  },
  // Masters
  {
    value: 'nutrition',
    label: 'Master Nutrition & Sécurité Alimentaire',
    href: '/masters/nutrition',
    type: 'master',
    faculty: 'FSAV',
    description: "Nutrition humaine et sécurité alimentaire pour la santé publique et le développement.",
    highlights: ['Nutrition humaine', 'Sécurité alimentaire', 'Santé publique'],
  },
  {
    value: 'developpement-rural',
    label: 'Master Développement Rural',
    href: '/masters/developpement-rural',
    type: 'master',
    faculty: 'FSAV',
    description: "Développement rural durable, politiques agricoles et renforcement des communautés.",
    highlights: ['Développement durable', 'Politiques agricoles', 'Communautés rurales'],
  },
  {
    value: 'agribusiness',
    label: 'Master Agribusiness',
    href: '/masters/agribusiness',
    type: 'master',
    faculty: 'FSAV',
    description: "Management et entrepreneuriat dans la chaîne de valeur agricole et agroalimentaire.",
    highlights: ['Agribusiness', 'Entrepreneuriat', 'Chaîne de valeur'],
  },
  // Masters FDSEG (Droit, Économie, Gestion)
  {
    value: 'droit-public',
    shortLabel: 'Master Droit Public',
    label: 'Master en Droit Public',
    href: '/masters/droit-public',
    type: 'master',
    faculty: 'FDSEG',
    description: "Formation de haut niveau en droit public, reconnue pour la solidité académique.",
    highlights: ['Droit public', 'Juridique', 'Pratique nationale et internationale'],
  },
  {
    value: 'droit-prive',
    shortLabel: 'Master Droit Privé',
    label: 'Master en Droit Privé',
    href: '/masters/droit-prive',
    type: 'master',
    faculty: 'FDSEG',
    description: "Expertise en droit privé pour les praticiens du marché national et international.",
    highlights: ['Droit privé', 'Juridique', 'Pratique'],
  },
  {
    value: 'politiques-economiques',
    shortLabel: 'Master Politiques Économiques',
    label: 'Master en Politiques Économiques',
    href: '/masters/politiques-economiques',
    type: 'master',
    faculty: 'FDSEG',
    description: "Analyse et conception des politiques économiques pour le développement.",
    highlights: ['Économie', 'Politiques publiques', 'Développement'],
  },
  {
    value: 'management-projets',
    shortLabel: 'Master Management des Projets',
    label: 'Master en Management des Projets',
    href: '/masters/management-projets',
    type: 'master',
    faculty: 'FDSEG',
    description: "Gestion de projets et pilotage d'organisations pour le secteur public et privé.",
    highlights: ['Gestion', 'Projets', 'Organisations'],
  },
  {
    value: 'banque-microfinance',
    shortLabel: 'Master Banque & Microfinance',
    label: 'Master en Banque & Microfinance',
    href: '/masters/banque-microfinance',
    type: 'master',
    faculty: 'FDSEG',
    description: "Banque, microfinance et ingénierie financière pour les professionnels du secteur.",
    highlights: ['Finance', 'Banque', 'Microfinance'],
  },
];
